import { Box, Button, Chip, CircularProgress, Divider, IconButton, Typography, useTheme } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import Fetch from "../services/Fetch";
import { AsyncPaginate } from "react-select-async-paginate";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { FormattedMessage } from "react-intl";
import { useConstants } from "../hooks/UseConstants";
import { parsePaginatedResponse } from "../utils/parsePaginatedResponse";

function TeachersFilter({ onClickClose, onClickConfirm, majorsValue, academicDegreeValue, setAcademicDegreeValue, setMajorsValue, setMajorId, setAcademicDegree, setTeacherSpecializations, teacherSpecializationsValue, setTeacherSpecializationsValue, filterWait, setFilterWait }) {
    const { language, host } = useConstants();
    const theme = useTheme();
    const isAr = language === "ar";

    const resetFilter = async () => {
        setAcademicDegreeValue(null);
        setTeacherSpecializationsValue(null);
        setMajorsValue(null);
        setMajorId('');
        setTeacherSpecializations('');
        setAcademicDegree('');
    };

    const loadMajors = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/majors?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const { items, pagination } = parsePaginatedResponse(response);
        const optionsFromApi = items.map((item) => ({
            value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
        }));
        return {
            options: [{ value: '', label: isAr ? 'الكل' : 'All' }, ...optionsFromApi],
            hasMore: pagination.current_page < pagination.last_page,
            additional: { page: page + 1 },
        };
    };

    const loadAcademicDegrees = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/academic-degrees?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const { items, pagination } = parsePaginatedResponse(response);
        const optionsFromApi = items.map((item) => ({
            value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
        }));
        return {
            options: [{ value: '', label: isAr ? 'الكل' : 'All' }, ...optionsFromApi],
            hasMore: pagination.current_page < pagination.last_page,
            additional: { page: page + 1 },
        };
    };

    const loadTeacherSpecializations = async (search, _loadedOptions, { page }) => {
        const response = await Fetch(`${host}/teacher-specializations?page=${page}${search ? `&search=${encodeURIComponent(search)}` : ""}`);
        const { items, pagination } = parsePaginatedResponse(response);
        const optionsFromApi = items.map((item) => ({
            value: item.id, label: language === 'en' ? item.name_en : item.name_ar,
        }));
        return {
            options: [{ value: '', label: isAr ? 'الكل' : 'All' }, ...optionsFromApi],
            hasMore: pagination.current_page < pagination.last_page,
            additional: { page: page + 1 },
        };
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.paper,
                width: { xs: "92%", sm: "82%", md: "60%" },
                maxWidth: 780,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 24px 60px rgba(13,53,140,0.18)",
            }}
            dir={isAr ? 'rtl' : 'ltr'}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0D358C 0%, #2563eb 100%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <FilterAltOutlinedIcon sx={{ fontSize: 30 }} />
                    <Box>
                        <Typography variant="h6" fontWeight={800}><FormattedMessage id='filtering_teachers' /></Typography>
                        <Typography variant="body2" sx={{ opacity: 0.88 }}>
                            {isAr ? "فلترة المعلمين حسب الاختصاص، التخصص التعليمي، والدرجة الأكاديمية." : "Filter teachers by specialization, major, and academic degree."}
                        </Typography>
                    </Box>
                </Box>
                <IconButton onClick={onClickClose} sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    <Chip label={isAr ? "فلترة أكاديمية" : "Academic filter"} color="primary" variant="outlined" />
                    <Chip label={isAr ? "اختصاص + تخصص + درجة" : "Specialization + major + degree"} variant="outlined" />
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mb: 3 }}>
                    <Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}><FormattedMessage id='specialization' /></Typography>
                        <AsyncPaginate
                            value={teacherSpecializationsValue || null}
                            loadOptions={loadTeacherSpecializations}
                            onChange={(option) => { setTeacherSpecializationsValue(option || null); setTeacherSpecializations(option?.value || '') }}
                            additional={{ page: 1 }}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: 40,
                                    borderRadius: 8,
                                    borderColor: "#cbd5e1",
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    color: 'black',
                                }),
                            }}
                            isSearchable
                            placeholder={isAr ? 'اختر الاختصاص' : 'Select specialization'}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}><FormattedMessage id='education_specialization' /></Typography>
                        <AsyncPaginate
                            value={majorsValue || null}
                            loadOptions={loadMajors}
                            onChange={(option) => { setMajorsValue(option || null); setMajorId(option?.value || '') }}
                            additional={{ page: 1 }}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: 40,
                                    borderRadius: 8,
                                    borderColor: "#cbd5e1",
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    color: 'black',
                                }),
                            }}
                            isSearchable
                            placeholder={isAr ? 'اختر التخصص التعليمي' : 'Select major'}
                        />
                    </Box>

                    <Box>
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}><FormattedMessage id='academic_degree' /></Typography>
                        <AsyncPaginate
                            value={academicDegreeValue || null}
                            loadOptions={loadAcademicDegrees}
                            onChange={(option) => { setAcademicDegreeValue(option || null); setAcademicDegree(option?.value || '') }}
                            additional={{ page: 1 }}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: 40,
                                    borderRadius: 8,
                                    borderColor: "#cbd5e1",
                                }),
                                option: (provided) => ({
                                    ...provided,
                                    color: 'black',
                                }),
                            }}
                            isSearchable
                            placeholder={isAr ? 'اختر الدرجة العلمية' : 'Select academic degree'}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, justifyContent: "flex-end" }}>
                    <Button variant="outlined" disabled={filterWait} sx={{ minWidth: 140 }} onClick={resetFilter}>
                        <FormattedMessage id='reset' />
                    </Button>
                    <Button
                        variant="contained"
                        disabled={filterWait}
                        sx={{ minWidth: 170, bgcolor: "#0D358C", "&:hover": { bgcolor: "#1d4ed8" } }}
                        onClick={() => { setFilterWait(true); onClickConfirm(); }}
                        startIcon={filterWait ? <CircularProgress size={18} color="inherit" /> : <FilterAltOutlinedIcon />}
                    >
                        {filterWait ? (isAr ? "جاري التطبيق..." : "Applying...") : <FormattedMessage id='filtering' />}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export default TeachersFilter;